import { useMutation, useQueryClient } from "@tanstack/react-query";
import { configuration } from "../../../configuration";
import { FetchError } from "../../types";
import { EditSurveyQuestionIsRequiredDto } from "../../types";
import { useAccessToken } from "../../use-access-token";
import { queryKeys } from "../queryKeys";

type Variables = {
  guid: string;
  surveyGuid: string;
  isRequired: boolean;
  modifiedByUserId: string;
};

const useEditSurveyQuestionIsRequiredRequest = () => {
  const { getAccessToken } = useAccessToken();

  async function editSurveyQuestionIsRequiredRequest(variables: Variables): Promise<void> {
    const accessToken = await getAccessToken();

    const body: EditSurveyQuestionIsRequiredDto = {
      isRequired: variables.isRequired,
      modifiedByUserId: variables.modifiedByUserId,
      surveyGuid: variables?.surveyGuid
    };

    const response = await fetch(`${configuration.apiRootUrl}/survey-questions/${variables.guid}/is-required`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      method: "put",
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new FetchError(response);
    }
  }

  return { editSurveyQuestionIsRequiredRequest };
};

const useEditSurveyQuestionIsRequired = () => {
  const { editSurveyQuestionIsRequiredRequest } = useEditSurveyQuestionIsRequiredRequest();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: editSurveyQuestionIsRequiredRequest,
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.surveys.getSurveyQuestion(variables.guid) });
      await queryClient.invalidateQueries({ queryKey: queryKeys.surveys.getSurveyQuestions(variables.surveyGuid) });
    },
  });
};

export { useEditSurveyQuestionIsRequired };
